import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { QUERY_KEYS } from '@/lib/constants';
import type { PaginatedResponse, ApiResponse, LanguageItem, ItemLog } from './types';

interface ItemsFilter {
  status?: string;
  type?: string;
  overdue?: boolean;
  deleted?: boolean;
  source_type?: string;
  cefr_level?: string;
  card_id?: string;
  offset?: number;
  limit?: number;
}

export function useItems(filter?: ItemsFilter) {
  const params = new URLSearchParams();
  if (filter?.status) params.set('status', filter.status);
  if (filter?.type) params.set('type', filter.type);
  if (filter?.overdue) params.set('overdue', 'true');
  if (filter?.deleted) params.set('deleted', 'true');
  if (filter?.source_type) params.set('source_type', filter.source_type);
  if (filter?.cefr_level) params.set('cefr_level', filter.cefr_level);
  if (filter?.card_id) params.set('card_id', filter.card_id);
  if (filter?.offset !== undefined) params.set('offset', String(filter.offset));
  if (filter?.limit) params.set('limit', String(filter.limit));
  const qs = params.toString();

  return useQuery({
    queryKey: [...QUERY_KEYS.items, filter ?? {}],
    queryFn: () => api.get<PaginatedResponse<LanguageItem>>(`/api/v1/items${qs ? `?${qs}` : ''}`),
    enabled: filter !== undefined,
  });
}

export function useItem(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.item(id),
    queryFn: () => api.get<ApiResponse<LanguageItem>>(`/api/v1/items/${id}`),
    select: (res) => res.data,
    enabled: !!id,
  });
}

export function useCreateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { text: string; item_type: string; definition?: string; example?: string; notes?: string; source_ref?: string; level?: number }) =>
      api.post<ApiResponse<LanguageItem>>('/api/v1/items', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEYS.items }),
  });
}

export function useUpdateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<LanguageItem>) =>
      api.patch<ApiResponse<{ success: boolean }>>(`/api/v1/items/${id}`, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.items });
      qc.invalidateQueries({ queryKey: QUERY_KEYS.item(variables.id) });
    },
  });
}

export function useDeleteItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/items/${id}`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.items });
      qc.invalidateQueries({ queryKey: QUERY_KEYS.item(id) });
    },
  });
}

export function useItemsByIds(ids: string[]) {
  const dedupedIds = [...new Set(ids)].sort();
  return useQuery({
    queryKey: ['items', 'batch', dedupedIds],
    queryFn: () => api.get<ApiResponse<LanguageItem[]>>(`/api/v1/items/batch?ids=${dedupedIds.join(',')}`),
    select: (res) => {
      const map = new Map<string, LanguageItem>();
      for (const item of res.data) map.set(item.id, item);
      return map;
    },
    enabled: dedupedIds.length > 0,
  });
}

export function useItemLogs(itemId: string | null) {
  return useQuery({
    queryKey: [...QUERY_KEYS.item(itemId!), 'logs'],
    queryFn: () => api.get<ApiResponse<ItemLog[]>>(`/api/v1/items/${itemId}/logs`),
    select: (res) => res.data,
    enabled: !!itemId,
  });
}

export function useBulkAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { action: 'archive' | 'learn' | 'move'; ids: string[]; target?: string }) =>
      api.post<{ affected: number }>('/api/v1/items/bulk', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEYS.items }),
  });
}
